/**
 * LedgerTab — the WIRED reservation ledger tab body (SCRUM-336, Step 6). A
 * `modules/booking` feature: a thin renderer over the pure `toLedgerView`
 * projection. It reads the two INDEPENDENT ledger regions and composes the
 * Tier-0 `LedgerLine` primitive + kit `Badge` (the panel already renders inside
 * the detail's `Card` chrome, so no nested Card here) — no new primitives, no
 * barrel widening (per plan).
 *
 * **Two independent regions (plan "two independent permission regions").**
 *   - Region A — the frozen line-item ledger, from `useReservationCharges`
 *     (`booking.reservations.read`). It self-describes its collection mode via
 *     the L1 `kind` discriminant, so it renders even when the folio is 403/404.
 *   - Region B — the cash summary + authoritative merchant badge, from
 *     `useReservationFolio` (`payments.folios.read`). Absent when the folio is
 *     forbidden / not-found; the line-item region still renders (the mapper's
 *     `collectionModel` falls back to the charges kind).
 *
 * **Dual-collection doctrine (plan).**
 *   - PropertyCollect → full frozen line-item ledger: Gross Room Rent,
 *     OTA commission(−, "Month-end AP liability"), Net Room Rent (bold), fee
 *     rows, tax rows, Total contract value (bold accent) + a cash summary
 *     (net collected / balance due — balance danger when > 0).
 *   - ChannelCollect → "$0.00 guest balance" note + Total net remittance
 *     ("expected wire deposit"). NO gross / commission in the cash ledger
 *     (money-of-record is the OTA; ADR 0037 amd(b)).
 *
 * **Honest NEED placeholders (plan "NEEDs").** Owner/PMC split, itemized
 * net-remittance, and channel gross / commission are deferred (Accounting
 * Phase 8 / channel Phase 11). They surface as labelled placeholders and
 * per-line inert-fee notes — NEVER a fabricated money figure. A phase-gated
 * (inert) fee is shown as a note, its "had-it-fired" amount is never rendered.
 *
 * **Honest states.** Loading skeleton; error + Retry (calls the hooks'
 * `refetch`); block / never-confirmed (both hooks notFound) → honest-empty
 * "no ledger yet"; folio 403 but charges ready → line items STILL render + an
 * honest cash-summary partial ("Payment/balance summary needs additional
 * access"). A `notFound` is NOT an error — no Retry affordance.
 *
 * **Money is `Cents` (CLAUDE.md rule 6 / FE rule §6).** Every figure is a
 * server passthrough from the mapper; `formatCents(cents, intl)` runs at the
 * display boundary only (never a hand-rolled `/100`, never FE arithmetic). A
 * `null` Cents renders the neutral "—" marker, never `formatCents(0)`.
 *
 * **Tokens (ADR frontend-0002/0003, SCRUM-78/79).** Semantic `var(--token)`
 * via arbitrary-value classes only; no raw hex/rgba, no inline-style numeric
 * literals, no pixel-literal arbitrary values.
 *
 * **A11y (WCAG 2.1 AA, FE rule 7).** Semantic HTML, headings/regions, status
 * conveyed by TEXT never color alone (the merchant badge always carries "PMC"
 * / "OTA" text), `aria-live` on the error region, axe-clean in light + dark.
 */

import * as React from 'react';
import { useIntl } from 'react-intl';
import { Badge, Button } from '@sparta/ui-kit';
import { formatCents } from '../../../shared/formatters/entity-change/values.js';
import { useReservationFolio } from '../api/use-reservation-folio.js';
import { useReservationCharges } from '../api/use-reservation-charges.js';
import { toLedgerView, type LedgerLineEntry, type LedgerView } from '../api/to-ledger-view.js';
import { LedgerLine } from '../_components/LedgerLine.js';

export interface LedgerTabProps {
  /** The reservation whose ledger to render (`/reservations/:id`). */
  readonly reservationId: string;
}

export function LedgerTab({ reservationId }: LedgerTabProps): React.ReactElement {
  const folio = useReservationFolio(reservationId);
  const charges = useReservationCharges(reservationId);

  // ── loading: either region still resolving ──────────────────────────────
  if (folio.status === 'loading' || charges.status === 'loading') {
    return <LedgerSkeleton />;
  }

  // ── error: only a genuine CHARGES failure blanks the whole tab ───────────
  // The charges region carries the ledger SHAPE (the L1 kind), so without it
  // there is nothing to render — a whole-tab error + Retry. A FOLIO failure
  // (error / 403) is region-independent: the line-item ledger still renders and
  // Region B (cash summary) degrades to an honest partial (handled below).
  // `notFound` is a distinct, non-error branch (honest-empty), NOT this Retry.
  if (charges.status === 'error') {
    const retry = (): void => {
      charges.refetch();
      folio.refetch();
    };
    return <LedgerError onRetry={retry} />;
  }

  // Region A (charges) is present only on `ready`; Region B (folio) only on
  // `ready`. Everything else (forbidden / notFound / error) contributes `null`
  // so the mapper degrades honestly (region independence).
  const folioData = folio.status === 'ready' ? folio.data : null;
  const chargesData = charges.status === 'ready' ? charges.data : null;

  const view = toLedgerView(folioData, chargesData);

  // ── honest-empty: never-confirmed (both regions absent) OR a block ───────
  // No folio and no charges = a pre-confirm / quote reservation with no ledger
  // yet. A block has a charges body but no meaningful ledger. Neither is an
  // error — no Retry affordance.
  const folioAbsent = folioData === null;
  const chargesAbsent = view.isChargesAbsent;
  if (view.isBlock || (folioAbsent && chargesAbsent)) {
    return <LedgerEmpty />;
  }

  // Region B degrades to an honest partial when the folio is forbidden (403) OR
  // errored — in both cases the cash summary cannot be shown, but we must not
  // fabricate a balance and must not blank the available line-item region. A
  // folio `notFound` (no folio yet) shows no partial (the ledger is pre-cash).
  const folioPartial = folio.status === 'forbidden' || folio.status === 'error';

  return <LedgerBody view={view} folioPartial={folioPartial} />;
}

/* ── loading skeleton ────────────────────────────────────────────────────── */

function LedgerSkeleton(): React.ReactElement {
  return (
    <div aria-busy="true" aria-live="polite" data-testid="skeleton">
      <span className="sr-only">Loading ledger…</span>
      <div className="flex flex-col gap-3">
        <span className="inline-block h-6 w-40 rounded bg-[var(--bg-sunken)]" aria-hidden="true" />
        {[0, 1, 2, 3].map((i) => (
          <span
            key={i}
            className="inline-block h-5 w-full rounded bg-[var(--bg-sunken)]"
            aria-hidden="true"
          />
        ))}
      </div>
    </div>
  );
}

/* ── error + retry ───────────────────────────────────────────────────────── */

function LedgerError({ onRetry }: { readonly onRetry: () => void }): React.ReactElement {
  return (
    <div role="alert" aria-live="assertive" className="flex items-center justify-between gap-4">
      <div>
        <h3 className="mb-1 text-base font-semibold text-[color:var(--fg-primary)]">
          Couldn’t load the ledger.
        </h3>
        <p className="text-sm text-[color:var(--fg-secondary)]">
          Check your connection and try again.
        </p>
      </div>
      <Button variant="secondary" onClick={onRetry}>
        Retry
      </Button>
    </div>
  );
}

/* ── honest-empty (never-confirmed / block) ──────────────────────────────── */

function LedgerEmpty(): React.ReactElement {
  return (
    <div className="flex flex-col items-center justify-center py-8 text-center">
      <h3 className="text-sm font-semibold text-[color:var(--fg-primary)]">No ledger yet</h3>
      <p className="mt-1 max-w-sm text-sm text-[color:var(--fg-secondary)]">
        This reservation isn’t confirmed, so there’s nothing to show here.
      </p>
    </div>
  );
}

/* ── ledger body: merchant badge + region A (line items) + region B (cash) ── */

function LedgerBody({
  view,
  folioPartial,
}: {
  readonly view: LedgerView;
  readonly folioPartial: boolean;
}): React.ReactElement {
  const isChannelCollect = view.collectionModel === 'ChannelCollect';

  return (
    <div className="flex flex-col gap-4">
      {/* Collection-mode heading + merchant badge on one row (design 212-215).
          The heading is canon verbatim copy (COPY.md:8). */}
      <div className="flex flex-wrap items-center gap-2.5">
        <h2 className="m-0 text-base font-bold text-[color:var(--fg-primary)]">
          {isChannelCollect ? 'Channel Collect ledger' : 'Wholesale transaction ledger'}
        </h2>
        <MerchantBadge badge={view.merchantBadge} />
      </div>

      {isChannelCollect ? (
        <ChannelCollectLedger view={view} />
      ) : (
        <PropertyCollectLedger view={view} folioPartial={folioPartial} />
      )}
    </div>
  );
}

/* ── merchant-of-record badge (text-carried, never color alone) ──────────── */

function MerchantBadge({
  badge,
}: {
  readonly badge: LedgerView['merchantBadge'];
}): React.ReactElement | null {
  if (badge === null) {
    return null;
  }
  // Status is conveyed by TEXT — the label spells out the record and the code
  // (PMC / OTA). Color (variant) is a redundant reinforcement only (WCAG 1.4.1).
  const label = badge === 'PMC' ? 'Merchant of record: PMC' : 'Merchant of record: OTA';
  const variant = badge === 'PMC' ? 'brand' : 'neutral';
  return (
    <div data-testid="merchant-badge">
      <Badge variant={variant}>{label}</Badge>
    </div>
  );
}

/* ── PropertyCollect: full frozen line-item ledger + cash summary ────────── */

function PropertyCollectLedger({
  view,
  folioPartial,
}: {
  readonly view: LedgerView;
  readonly folioPartial: boolean;
}): React.ReactElement {
  return (
    <div className="flex flex-col gap-4">
      {/* Flat ledger: owner-matrix + fee/tax + total render as ONE sequence of
          LedgerLine rows (design reservations.jsx:227-234), each self-separated
          by its own border-b — no intermediate section headings. */}
      <div className="flex flex-col">
        <OwnerMatrixLines lines={view.ownerMatrixLines} />
        <ChargeLines feeLines={view.feeLines} taxLines={view.taxLines} />
        <TotalContractValue cents={view.totalContractValueCents} />
      </div>
      <CashSummary cashSummary={view.cashSummary} folioPartial={folioPartial} />
      <NeedPlaceholders needs={view.needs} />
    </div>
  );
}

/** L2 wholesale owner-matrix lines — Gross / commission(−) / Net Room Rent.
 *  The commission line is passed through NEGATIVE (the `LedgerLine` paints it
 *  danger) and annotated as a month-end AP liability. Rendered as a FLAT list
 *  of `LedgerLine` rows — no invented section heading (design
 *  reservations.jsx:227-234 is a flat ledger). */
function OwnerMatrixLines({
  lines,
}: {
  readonly lines: LedgerLineEntry[] | null;
}): React.ReactElement | null {
  if (lines === null) {
    return null;
  }
  return (
    <>
      {lines.map((line) => {
        const isCommission = line.label === 'OTA Commission';
        const isNet = line.label === 'Net Room Rent';
        return (
          <LedgerLine
            key={line.label}
            label={line.label}
            valueCents={line.valueCents}
            note={isCommission ? 'Month-end AP liability' : undefined}
            bold={isNet}
          />
        );
      })}
    </>
  );
}

/** L1 fee + tax rows (guest-facing name + passthrough Cents). Flat list — no
 *  invented section heading (design reservations.jsx:227-234). */
function ChargeLines({
  feeLines,
  taxLines,
}: {
  readonly feeLines: LedgerLineEntry[];
  readonly taxLines: LedgerLineEntry[];
}): React.ReactElement | null {
  if (feeLines.length === 0 && taxLines.length === 0) {
    return null;
  }
  return (
    <>
      {feeLines.map((line) => (
        <LedgerLine key={`fee-${line.label}`} label={line.label} valueCents={line.valueCents} />
      ))}
      {taxLines.map((line) => (
        <LedgerLine key={`tax-${line.label}`} label={line.label} valueCents={line.valueCents} />
      ))}
    </>
  );
}

/** Total contract value — `breakdown.grandTotalCents` passthrough (bold accent).
 *  `null` (charges absent / non-computed) renders the neutral "—" marker. */
function TotalContractValue({
  cents,
}: {
  readonly cents: number | null;
}): React.ReactElement | null {
  if (cents === null) {
    return null;
  }
  return (
    <div className="border-t border-[var(--border-subtle)] pt-2">
      <LedgerLine label="Total contract value" valueCents={cents} bold accent />
    </div>
  );
}

/* ── ChannelCollect: $0 guest balance + total net remittance ─────────────── */

function ChannelCollectLedger({ view }: { readonly view: LedgerView }): React.ReactElement {
  const intl = useIntl();
  // On ChannelCollect the guest pays the OTA directly, so the on-platform
  // guest balance is a structural $0.00 (ADR 0037 amd(b) — the literal zero the
  // channel-remittance body also carries as `guestBalanceCents: 0`). This is
  // the real zero, not a fabricated figure. Rendered through `formatCents` at
  // the display boundary like every other money value.
  const zeroBalance = formatCents(0, intl);

  return (
    <div className="flex flex-col gap-4">
      <p className="text-sm text-[color:var(--fg-secondary)]">
        Guest balance{' '}
        <span className="font-semibold tabular-nums text-[color:var(--fg-primary)]">
          {zeroBalance}
        </span>{' '}
        — the guest paid the booking platform directly (it is the merchant of record).
      </p>

      <div className="border-t border-[var(--border-subtle)] pt-2">
        <LedgerLine
          label="Net remittance"
          valueCents={view.netRemittanceCents ?? Number.NaN}
          note="Expected wire deposit"
          bold
          accent
        />
      </div>

      <NeedPlaceholders needs={view.needs} />
    </div>
  );
}

/* ── Region B: folio cash summary (net collected / balance due) ──────────── */

function CashSummary({
  cashSummary,
  folioPartial,
}: {
  readonly cashSummary: LedgerView['cashSummary'];
  readonly folioPartial: boolean;
}): React.ReactElement {
  // Region B is independent of Region A. When the folio is forbidden (403) OR
  // errored the cash summary cannot render — show an honest partial rather than
  // fabricate a balance. When it is merely not-found the cashSummary is null
  // and no partial is shown (the ledger is simply pre-cash).
  if (cashSummary === null) {
    if (folioPartial) {
      return (
        <section
          aria-labelledby="ledger-cash-heading"
          className="border-t border-[var(--border-subtle)] pt-3"
        >
          <h3
            id="ledger-cash-heading"
            className="mb-1 text-xs font-bold uppercase tracking-[0.08em] text-[color:var(--fg-tertiary)]"
          >
            Payment summary
          </h3>
          <p className="text-sm text-[color:var(--fg-secondary)]">
            Payment/balance summary needs additional access.
          </p>
        </section>
      );
    }
    return <></>;
  }

  const balanceOutstanding = cashSummary.balanceDueCents > 0;

  return (
    <section
      aria-labelledby="ledger-cash-heading"
      className="border-t border-[var(--border-subtle)] pt-3"
    >
      <h3
        id="ledger-cash-heading"
        className="mb-1 text-xs font-bold uppercase tracking-[0.08em] text-[color:var(--fg-tertiary)]"
      >
        Payment summary
      </h3>
      <LedgerLine label="Net collected" valueCents={cashSummary.netCollectedCents} />
      <LedgerLine
        // Balance due is a positive amount still owing — rendered as the real
        // server Cents figure (no FE arithmetic, no sign inversion). An
        // outstanding balance (> 0) is conveyed by the explicit "Outstanding"
        // note + bold weight, NEVER by color alone (WCAG 1.4.1).
        label="Balance due"
        valueCents={cashSummary.balanceDueCents}
        note={balanceOutstanding ? 'Outstanding' : undefined}
        bold={balanceOutstanding}
      />
    </section>
  );
}

/* ── honest NEED placeholders (no fabricated data) ───────────────────────── */

function NeedPlaceholders({ needs }: { readonly needs: LedgerView['needs'] }): React.ReactElement {
  // Exactly ONE Accounting (Phase 8) placeholder and ONE distribution (Phase 11)
  // placeholder. The owner/manager net-rent split and the itemized net
  // remittance are BOTH gated on Accounting, so they share a single honest
  // Phase-8 line (never two — a screen reader hears one deferral, and the money
  // is never fabricated). The badge already carries the PMC / OTA tokens, so
  // this copy deliberately avoids those abbreviations.
  return (
    <section
      aria-labelledby="ledger-needs-heading"
      className="border-t border-[var(--border-subtle)] pt-3"
    >
      <h3
        id="ledger-needs-heading"
        className="mb-1 text-xs font-bold uppercase tracking-[0.08em] text-[color:var(--fg-tertiary)]"
      >
        Coming soon
      </h3>
      <ul className="flex flex-col gap-2 text-sm text-[color:var(--fg-tertiary)]">
        <li data-testid="need-accounting">
          Owner and manager net-rent split, plus the itemized net remittance —{' '}
          {needs.ownerPmcSplit.label}
        </li>
        <li data-testid="need-distribution">
          Gross and commission from the distribution side — {needs.channelGross.label}
        </li>
      </ul>
    </section>
  );
}
